import React, { useState, useEffect, useRef } from 'react';
import ProtectedLayout from '../components/ProtectedLayout';
import api from '../api';
import { toast } from 'sonner';

const HEALTH_CLASSES = [
  { value: 'PP', label: 'Preferred Plus' }, { value: 'P', label: 'Preferred' },
  { value: 'RP', label: 'Regular Plus' }, { value: 'R', label: 'Regular' },
  ...Array.from({ length: 16 }, (_, i) => ({ value: `T${i + 1}`, label: `Table ${i + 1}` })),
];

const NICOTINE_OPTIONS = [
  { value: '-1', label: 'Never used' }, { value: '0', label: 'Current user' },
  { value: '1', label: 'Last used 1 year ago' }, { value: '2', label: 'Last used 2 years ago' },
  { value: '3', label: 'Last used 3 years ago' }, { value: '4', label: 'Last used 4 years ago' },
  { value: '5', label: 'Last used 5+ years ago' },
];

function GenderButton({ value, selected, onChange }) {
  return (
    <label style={{ flex: 1, cursor: 'pointer' }}>
      <input type="radio" name="gender_code_v2" value={value} checked={selected} onChange={() => onChange(value)} style={{ display: 'none' }} />
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        border: `1px solid ${selected ? '#2D87F0' : '#DDE7F1'}`,
        padding: '12px', borderRadius: '8px', cursor: 'pointer',
        background: selected ? '#EFF6FF' : 'white',
        color: selected ? '#2D87F0' : '#64748B',
        fontWeight: selected ? '600' : '400',
        transition: 'all 150ms',
      }}>
        {value === 'M' ? 'Male' : 'Female'}
      </div>
    </label>
  );
}

// ============================================================
// UW SEARCH (inlined for self-containment)
// ============================================================
const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
  background: 'white', border: '1px solid #DDE7F1', borderRadius: '6px',
  boxShadow: 'var(--shadow-dropdown)', maxHeight: '220px', overflowY: 'auto',
  marginTop: '2px', padding: '8px 12px',
};

function UwItemRow({ item, index, setUwItems }) {
  const { useState, useEffect } = React;
  const isExpanded = item.active_question > 0;
  const currentQ = item.questions?.[item.active_question - 1];
  const update = (updater) => setUwItems(prev => { const next = [...prev]; next[index] = updater(next[index]); return next; });
  const toggleEdit = () => update(it => ({ ...it, active_question: it.active_question > 0 ? 0 : 1 }));
  const remove = () => setUwItems(prev => prev.filter((_, i) => i !== index));
  const updateQ = (qIndex, changes) => update(it => { const qs = [...(it.questions || [])]; qs[qIndex] = { ...qs[qIndex], ...changes }; return { ...it, questions: qs }; });
  const displayName = item.title !== item.search_term ? item.search_term : item.title;
  const [timingError, setTimingError] = useState(false);
  const [yesNoError, setYesNoError] = useState(false);

  // #5 Step indicator: drugs have 2 steps, conditions have 1
  const totalSteps = item.type_id === 1 ? 2 : 1;
  const currentStep = item.active_question;

  // #2 Is this the final question? (drug_last_taken and condition_last_treated are always last)
  const isLastQuestion = currentQ && currentQ.type !== 'drug_indication';

  // #3 Completion summary
  const getSummary = () => {
    if (!item.completed || !item.questions?.length) return null;
    const parts = [];
    const indicationQ = item.questions.find(q => q.type === 'drug_indication');
    if (indicationQ?.answer) {
      const ind = item.indications?.find(i => i.id === indicationQ.answer);
      parts.push(ind ? ind.title : indicationQ.answer === 'other' ? 'Other' : null);
    }
    const timingQ = item.questions.find(q => ['drug_last_taken', 'condition_last_treated'].includes(q.type));
    if (timingQ) {
      if (timingQ.currently === 'yes') {
        parts.push(item.type_id === 1 ? 'Currently taking' : 'Currently has');
      } else if (timingQ.last_treated_approx) {
        const v = timingQ.last_treated_approx;
        parts.push(v === '0' ? 'Within last 12 months' : `~${v} year${parseInt(v) > 1 ? 's' : ''} ago`);
      } else if (timingQ.last_treated_month || timingQ.last_treated_year) {
        const d = [timingQ.last_treated_month, timingQ.last_treated_day, timingQ.last_treated_year].filter(Boolean).join('/');
        parts.push(d);
      }
    }
    return parts.filter(Boolean).join(' · ');
  };
  const summary = getSummary();

  const handleNext = async (skip = false) => {
    let updated = { ...item };
    if (currentQ && !skip) {
      const q = { ...currentQ };
      if (['drug_last_taken', 'condition_last_treated'].includes(q.type)) {
        if (!q.currently) {
          setYesNoError(true);
          return;
        }
        setYesNoError(false);
        if (q.currently === 'yes') {
          q.answer = 'now';
        } else {
          // Validate: require either approximate OR (month + year)
          const hasApprox = !!q.last_treated_approx;
          const hasExact = !!(q.last_treated_month && q.last_treated_year);
          if (!hasApprox && !hasExact) {
            setTimingError(true);
            return;
          }
          setTimingError(false);
          if (hasExact) {
            q.answer = `${q.last_treated_month || ''}/${q.last_treated_day || ''}/${q.last_treated_year || ''}`;
          } else {
            q.answer = 'approx:' + q.last_treated_approx;
          }
        }
      }
      updated.questions = [...(updated.questions || [])];
      updated.questions[item.active_question - 1] = q;
      if (q.type === 'drug_indication') {
        try {
          const res = await api.post('/quoter/uw-next-question', { uw_item: updated });
          if (res.data.next) {
            const nextQ = { ...res.data.next, answer: [], currently: '', last_treated_approx: '', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
            updated.questions = [...updated.questions, nextQ];
            updated.active_question = updated.questions.length;
          } else { updated.completed = true; updated.active_question = 0; }
        } catch (e) { updated.active_question = 0; }
        update(() => updated); return;
      }
    }
    if (updated.active_question < (updated.questions?.length || 0)) { updated.active_question += 1; update(() => updated); return; }
    try {
      const res = await api.post('/quoter/uw-next-question', { uw_item: updated });
      if (res.data.next) {
        const nextQ = { ...res.data.next, answer: [], currently: '', last_treated_approx: '', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
        if (res.data.indications) updated.indications = res.data.indications;
        updated.questions = [...(updated.questions || []), nextQ];
        updated.active_question = updated.questions.length;
      } else { updated.completed = true; updated.active_question = 0; }
    } catch (e) { updated.active_question = 0; }
    update(() => updated);
  };

  // #4 Auto-advance when "Yes" is selected (500ms delay)
  useEffect(() => {
    if (currentQ && ['drug_last_taken', 'condition_last_treated'].includes(currentQ.type) && currentQ.currently === 'yes') {
      const timer = setTimeout(() => handleNext(), 600);
      return () => clearTimeout(timer);
    }
  }, [currentQ?.currently]);

  return (
    <div style={{ marginBottom: isExpanded ? '0' : '12px' }}>
      {/* Item header chip */}
      <div style={{ border: '1px solid #DDE7F1', borderRadius: isExpanded ? '10px 10px 0 0' : '10px', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', background: isExpanded ? 'rgba(45,135,240,0.08)' : 'white', position: 'relative', zIndex: 10, transition: 'background 150ms' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className={item.type_id === 1 ? 'fas fa-prescription-bottle-medical' : 'fas fa-book-medical'} style={{ color: '#2D87F0', fontSize: '20px', flexShrink: 0 }}></i>
            <span style={{ fontWeight: '700', fontSize: '16px' }}>{displayName}</span>
            {item.completed && <i className="fas fa-check-circle" style={{ color: '#16A34A', fontSize: '16px' }}></i>}
          </div>
          {/* #3 Completion summary */}
          {item.completed && summary && (
            <div style={{ fontSize: '13px', color: '#64748B', paddingLeft: '32px', marginTop: '4px', fontStyle: 'italic' }}>
              {summary}
            </div>
          )}
          {!item.completed && item.title !== item.search_term && (
            <div style={{ fontSize: '13px', color: '#64748B', paddingLeft: '32px', marginTop: '2px' }}>({item.title})</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
          <button onClick={toggleEdit} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '16px', padding: '4px 8px' }} onMouseEnter={e => e.target.style.color = '#2D87F0'} onMouseLeave={e => e.target.style.color = '#64748B'}><i className="fas fa-edit"></i></button>
          <button onClick={remove} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '16px', padding: '4px 8px' }} onMouseEnter={e => e.target.style.color = '#DC2626'} onMouseLeave={e => e.target.style.color = '#64748B'}><i className="fas fa-times"></i></button>
        </div>
      </div>

      {/* Question panel */}
      {isExpanded && currentQ && (
        <div style={{ border: '1px solid #DDE7F1', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '22px 20px', background: 'white', display: 'flex', flexDirection: 'column', gap: '18px', marginBottom: '12px' }}>

          {/* Question panel body */}

          {/* drug_indication question */}
          {currentQ.type === 'drug_indication' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontWeight: '600', fontSize: '16px', lineHeight: '1.4' }}>What are you taking <em>{displayName}</em> for?</div>
              {(item.indications || []).map(ind => (
                <label key={ind.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '16px', padding: '6px 0' }}>
                  <input type="radio" name={`ind_v2_${item.id}_${index}`} value={ind.id} checked={currentQ.answer === ind.id} onChange={() => updateQ(item.active_question - 1, { answer: ind.id })} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                  {ind.title}
                </label>
              ))}
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '16px', padding: '6px 0' }}>
                <input type="radio" name={`ind_v2_${item.id}_${index}`} value="other" checked={currentQ.answer === 'other'} onChange={() => updateQ(item.active_question - 1, { answer: 'other' })} style={{ width: '18px', height: '18px', cursor: 'pointer' }} />Other
              </label>
            </div>
          )}

          {/* drug_last_taken / condition_last_treated */}
          {(currentQ.type === 'drug_last_taken' || currentQ.type === 'condition_last_treated') && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ fontWeight: '600', fontSize: '16px', lineHeight: '1.4' }}>
                {currentQ.type === 'drug_last_taken' ? <>Are you currently taking <em>{displayName}</em>?</> : <>Do you currently have <em>{displayName}</em>?</>}
              </div>

              {/* Yes / No — separated, subtle style */}
              <div style={{ display: 'inline-flex', gap: '10px' }}>
                {['yes', 'no'].map(v => (
                  <label key={v} style={{ cursor: 'pointer' }}>
                    <input type="radio" value={v} checked={currentQ.currently === v} onChange={() => { updateQ(item.active_question - 1, { currently: v }); setYesNoError(false); }} style={{ display: 'none' }} />
                    <div style={{
                      display: 'flex', justifyContent: 'center', alignItems: 'center',
                      padding: '7px 20px',
                      border: `1px solid ${currentQ.currently === v ? '#2D87F0' : '#94A3B8'}`,
                      background: currentQ.currently === v ? '#EFF6FF' : 'white',
                      color: currentQ.currently === v ? '#2D87F0' : '#64748B',
                      fontSize: '14px', fontWeight: currentQ.currently === v ? '600' : '400',
                      cursor: 'pointer', borderRadius: '8px', transition: 'all 150ms',
                    }}>{v === 'yes' ? 'Yes' : 'No'}</div>
                  </label>
                ))}
              </div>

              {yesNoError && (
                <div style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '-10px' }}>
                  <i className="fas fa-exclamation-circle"></i>
                  Please select Yes or No before continuing.
                </div>
              )}

              {/* Only show timing section when No is selected */}
              {currentQ.currently === 'no' && (
                <>
                  {/* Divider between Q1 (Yes/No) and Q2 (When) */}
                  <div style={{ height: '1px', background: '#DDE7F1', margin: '4px 0' }}></div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ fontWeight: '600', fontSize: '16px', lineHeight: '1.4' }}>
                    {currentQ.type === 'drug_last_taken' ? <>When did you last take <em>{displayName}</em>?</> : <>When were you last treated for <em>{displayName}</em>?</>}
                  </div>

                  {/* Approximate */}
                  <div style={{ position: 'relative' }}>
                    <select className="input-field" value={currentQ.last_treated_approx || ''} onChange={e => { updateQ(item.active_question - 1, { last_treated_approx: e.target.value, last_treated_month: '', last_treated_day: '', last_treated_year: '' }); setTimingError(false); }} style={{ appearance: 'none', cursor: 'pointer', fontSize: '16px', height: '52px', paddingRight: '36px' }}>
                      <option value="">Select a time frame</option>
                      <option value="0">Within the last 12 months</option>
                      {[1, 2, 3, 4, 5, 10].map(y => <option key={y} value={String(y)}>{y} year{y > 1 ? 's' : ''} ago</option>)}
                    </select>
                    <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '13px', pointerEvents: 'none' }}></i>
                  </div>

                  {/* Divider */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ flex: 1, height: '1px', background: '#DDE7F1' }}></div>
                    <span style={{ fontSize: '15px', color: '#475569', fontWeight: '600' }}>— or —</span>
                    <div style={{ flex: 1, height: '1px', background: '#DDE7F1' }}></div>
                  </div>

                  {/* Exact date */}
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '8px', whiteSpace: 'nowrap' }}>
                      Exact date <span style={{ fontWeight: '500', color: '#475569', fontSize: '13px' }}>(month &amp; year required, day optional)</span>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <select className="input-field" value={currentQ.last_treated_month || ''} onChange={e => { updateQ(item.active_question - 1, { last_treated_month: e.target.value, last_treated_approx: '' }); setTimingError(false); }} style={{ appearance: 'none', cursor: 'pointer', fontSize: '16px', height: '52px', paddingRight: '28px' }}>
                          <option value="">MM</option>
                          {[['01','January'],['02','February'],['03','March'],['04','April'],['05','May'],['06','June'],['07','July'],['08','August'],['09','September'],['10','October'],['11','November'],['12','December']].map(([v,l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                      </div>
                      <div style={{ position: 'relative', flex: 1 }}>
                        <select className="input-field" value={currentQ.last_treated_day || ''} onChange={e => updateQ(item.active_question - 1, { last_treated_day: e.target.value })} style={{ appearance: 'none', cursor: 'pointer', fontSize: '16px', height: '52px', paddingRight: '28px', borderStyle: 'dashed', borderColor: '#CBD5E1' }}>
                          <option value="">DD</option>
                          {Array.from({ length: currentQ.last_treated_month ? new Date(parseInt(currentQ.last_treated_year) || new Date().getFullYear(), parseInt(currentQ.last_treated_month), 0).getDate() : 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '12px', pointerEvents: 'none' }}></i>
                      </div>
                      <div style={{ position: 'relative', flex: 2 }}>
                        <select className="input-field" value={currentQ.last_treated_year || ''} onChange={e => { updateQ(item.active_question - 1, { last_treated_year: e.target.value, last_treated_approx: '' }); setTimingError(false); }} style={{ appearance: 'none', cursor: 'pointer', fontSize: '16px', height: '52px', paddingRight: '28px' }}>
                          <option value="">YYYY</option>
                          {Array.from({ length: new Date().getFullYear() - 1949 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                  </div>
                </div>
                </>
              )}
            </div>
          )}

          {/* #2 Done/Next button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
            {timingError && (
              <div style={{ fontSize: '13px', color: '#DC2626', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="fas fa-exclamation-circle"></i>
                Please select an approximate time or enter a month and year before continuing.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* #2 Done on last question, Next otherwise */}
            <button onClick={() => handleNext(false)} className="btn-primary" style={{ padding: '10px 28px', fontSize: '16px', height: '46px', borderRadius: '8px' }}>
              Next
            </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function UwSearchSection({ uwItems, setUwItems, typeId, label, placeholder, showItems = true, initialSearch = '' }) {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [searchState, setSearchState] = useState(0);
  const [results, setResults] = useState([]);
  const [lastTerm, setLastTerm] = useState('');
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Auto-trigger search when initialSearch is provided
  useEffect(() => {
    if (initialSearch && initialSearch.length >= 3) {
      doSearch(initialSearch);
    }
  }, [initialSearch]);

  const doSearch = async (term) => {
    if (term.length <= 2) { setSearchState(0); return; }
    setLastTerm(term);
    setSearchState(1);
    try {
      const res = await api.post('/quoter/uw-search', { search: term });
      if (term !== inputRef.current?.value) return;
      // Filter by typeId if provided
      const filtered = typeId
        ? (res.data.results || []).filter(r => r.type_id === typeId)
        : (res.data.results || []);
      setResults(filtered);
      setSearchState(2);
      if (resultsRef.current) resultsRef.current.scrollTop = 0;
    } catch (e) { setSearchState(0); }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 400);
  };

  const selectResult = async (result) => {
    const item = { ...result, active_question: 1, questions: [], completed: false };
    setSearchTerm(''); setSearchState(0); setResults([]);
    try {
      const res = await api.post('/quoter/uw-next-question', { uw_item: item });
      if (res.data.next) {
        const q = { ...res.data.next, answer: [], currently: '', last_treated_approx: '', last_treated_month: '', last_treated_day: '', last_treated_year: '' };
        if (res.data.indications) item.indications = res.data.indications;
        item.questions = [q]; item.active_question = 1;
      }
    } catch (e) {}
    setUwItems(prev => [...prev, item]);
  };

  const icon = typeId === 1 ? 'fas fa-prescription-bottle-medical' : typeId === 2 ? 'fas fa-book-medical' : 'fas fa-search';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
      {/* Column header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <i className={icon} style={{ color: '#2D87F0', fontSize: '16px' }}></i>
        <span style={{ fontSize: '14px', fontWeight: '600', color: '#0F172A' }}>{label}</span>
      </div>

      {/* Search input */}
      <div style={{ position: 'relative', zIndex: 20, marginBottom: '12px' }}>
        <input
          ref={inputRef}
          type="search"
          className="input-field"
          placeholder={placeholder || 'Search...'}
          value={searchTerm}
          onChange={handleInput}
          style={{ height: '48px', fontSize: '15px' }}
        />
        {searchState === 1 && <div style={dropdownStyle}><span style={{ color: '#64748B', fontSize: '15px' }}>Searching...</span></div>}
        {searchState === 2 && results.length === 0 && <div style={dropdownStyle}><span style={{ color: '#64748B', fontSize: '15px' }}>No results found for &ldquo;{lastTerm}&rdquo;.</span></div>}
        {searchState === 2 && results.length > 0 && (
          <div style={{ ...dropdownStyle, padding: 0 }} ref={resultsRef}>
            {results.map(r => (
              <button key={r.id} onMouseDown={() => selectResult(r)} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #F1F5F9', fontSize: '15px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                <i className={r.type_id === 1 ? 'fas fa-prescription-bottle-medical' : 'fas fa-book-medical'} style={{ color: '#2D87F0', fontSize: '18px', width: '18px' }}></i>
                <div>
                  <div style={{ fontWeight: '600' }}>{r.title !== r.search_term ? r.search_term : r.title}</div>
                  {r.title !== r.search_term && <div style={{ color: '#64748B', fontSize: '13px' }}>({r.title})</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Selected items — only show incomplete/in-progress items inside the card */}
      {showItems && (
        <div>
          {uwItems.filter(item => !item.completed).map((item, i) => {
            const realIndex = uwItems.indexOf(item);
            return <UwItemRow key={item.id + realIndex} item={item} index={realIndex} setUwItems={setUwItems} />;
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// UW HISTORY MODAL (inlined)
// ============================================================
function UwHistoryModal({ visible, onClose, form, answers, setAnswers }) {
  const [activeTab, setActiveTab] = React.useState(0);
  const [fieldUwItems, setFieldUwItems] = React.useState({});
  const [otherOpen, setOtherOpen] = React.useState({});
  const sectionRefs = React.useRef([]);
  const scrollContainerRef = React.useRef(null);
  const summaryRef = React.useRef(null);

  React.useEffect(() => {
    if (visible) setActiveTab(0);
  }, [visible]);

  // Map question field keys to condition names for auto-prefill
  const FIELD_TO_CONDITION = {
    heart_attack:      'Heart Attack',          stroke:            'Stroke',
    heart_failure:     'Heart Failure',         coronary_artery:   'Coronary Artery Disease',
    heart_surgery:     'Heart Surgery',         angina:            'Angina',
    afib:              'Atrial Fibrillation',   pacemaker:         'Pacemaker',
    peripheral_artery: 'Peripheral Artery Disease', hypertension:  'Hypertension',
    high_cholesterol:  'High Cholesterol',      copd:              'COPD',
    asthma:            'Asthma',                sleep_apnea:       'Sleep Apnea',
    pulm_fibrosis:     'Pulmonary Fibrosis',    pulm_embolism:     'Pulmonary Embolism',
    cancer:            'Cancer',                diabetes:          'Type 2 Diabetes',
    kidney:            'Kidney Disease',        liver:             'Liver Disease',
    hiv:               'HIV',                   lupus:             'Lupus',
    ms:                'Multiple Sclerosis',    epilepsy:          'Epilepsy',
    parkinsons:        "Parkinson's Disease",   thyroid:           'Hypothyroidism',
    blood_disorder:    'Anemia',                depression:        'Depression',
    anxiety:           'Anxiety',               bipolar:           'Bipolar Disorder',
    ptsd:              'PTSD',                  substance:         'Substance Abuse',
  };

  // Per-field UW item helpers
  const getFieldItems = (fieldKey, type) => fieldUwItems[fieldKey]?.[type] || [];
  const setFieldItems = (fieldKey, type) => (updater) =>
    setFieldUwItems(prev => ({
      ...prev,
      [fieldKey]: {
        ...prev[fieldKey],
        [type]: typeof updater === 'function' ? updater(prev[fieldKey]?.[type] || []) : updater,
      },
    }));


  if (!visible || !form) return null;
  const subsections = form?.details?.sections?.[1]?.subsections || [];

  const SECTION_ICONS = {
    cardiovascular:   'fas fa-heart',
    respiratory:      'fas fa-lungs',
    other_conditions: 'fas fa-stethoscope',
    mental_health:    'fas fa-brain',
    lifestyle:        'fas fa-person-running',
  };

  const checkVisibility = (field) => {
    if (!field?.visibility_logic?.conditions?.length) return true;
    const conds = field.visibility_logic.conditions;
    const op = field.visibility_logic.operator || 'and';
    const check = (c) => answers[c.field_key] === c.value;
    return op === 'or' ? conds.some(check) : conds.every(check);
  };

  const setAnswer = (key, value) => setAnswers(prev => ({ ...prev, [key]: value }));
  // Collect all entered items across all fields (including "Other" sections)
  const allConditions = Object.values(fieldUwItems).flatMap(f => f?.conds || []);
  const allMedications = Object.values(fieldUwItems).flatMap(f => f?.meds || []);

  // Generate completion summary text for an item
  const getItemSummary = (item) => {
    if (!item.completed || !item.questions?.length) return null;
    const parts = [];
    const indicationQ = item.questions.find(q => q.type === 'drug_indication');
    if (indicationQ?.answer) {
      const ind = item.indications?.find(i => i.id === indicationQ.answer);
      if (ind) parts.push(ind.title);
      else if (indicationQ.answer === 'other') parts.push('Other');
    }
    const timingQ = item.questions.find(q => ['drug_last_taken','condition_last_treated'].includes(q.type));
    if (timingQ) {
      if (timingQ.currently === 'yes') parts.push(item.type_id === 1 ? 'Currently taking' : 'Currently has');
      else if (timingQ.last_treated_approx) {
        const v = timingQ.last_treated_approx;
        parts.push(v === '0' ? 'Within last 12 months' : `~${v} year${parseInt(v) > 1 ? 's' : ''} ago`);
      } else if (timingQ.last_treated_month || timingQ.last_treated_year) {
        parts.push([timingQ.last_treated_month, timingQ.last_treated_day, timingQ.last_treated_year].filter(Boolean).join('/'));
      }
    }
    return parts.filter(Boolean).join(' · ') || null;
  };

  const scrollToSummary = () => {
    if (summaryRef.current && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: summaryRef.current.offsetTop - 16, behavior: 'smooth' });
    }
  };

  const handleTabClick = (i) => {
    const el = sectionRefs.current[i];
    if (el && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: el.offsetTop - 72, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const scrollTop = scrollContainerRef.current.scrollTop;
    let current = 0;
    sectionRefs.current.forEach((ref, i) => {
      if (ref && ref.offsetTop - 80 <= scrollTop) current = i;
    });
    setActiveTab(current);
  };

  const YesNoButtons = ({ name, value, onChange }) => (
    <div style={{ display: 'inline-flex', gap: '10px', flexShrink: 0 }}>
      {['no','yes'].map(v => {
        const isSel = value === v;
        return (
          <label key={v} style={{ cursor: 'pointer' }}>
            <input type="radio" name={name} value={v} checked={isSel} onChange={() => onChange(v)} style={{ display: 'none' }} />
            <div style={{ padding: '8px 22px', fontSize: '15px', fontWeight: isSel ? '600' : '500', border: '1px solid ' + (isSel ? '#2D87F0' : '#94A3B8'), background: isSel ? '#EFF6FF' : 'white', color: isSel ? '#2D87F0' : '#64748B', cursor: 'pointer', borderRadius: '8px', transition: 'all 150ms' }}>
              {v === 'yes' ? 'Yes' : 'No'}
            </div>
          </label>
        );
      })}
    </div>
  );

  const FollowUp = ({ fieldKey }) => {
    const allItems = [...getFieldItems(fieldKey, 'conds'), ...getFieldItems(fieldKey, 'meds')];
    const setAllItems = (updater) => {
      const updated = typeof updater === 'function' ? updater(allItems) : updater;
      setFieldItems(fieldKey, 'conds')(() => updated.filter(i => i.type_id === 2));
      setFieldItems(fieldKey, 'meds')(() => updated.filter(i => i.type_id === 1));
    };

    const hasCompletedItems = allItems.some(i => i.completed);
    const hasInProgressItems = allItems.some(i => !i.completed);
    if (hasCompletedItems && !hasInProgressItems) return null;

    // Pre-fill the search with the mapped condition name
    const prefill = FIELD_TO_CONDITION[fieldKey] || '';

    return (
      <div style={{ marginTop: '8px', marginBottom: '4px', padding: '0 24px' }}>
        <div className="portal-card" style={{ padding: '16px 20px' }}>
          <UwSearchSection
            uwItems={allItems}
            setUwItems={setAllItems}
            label="Enter a health condition or medication"
            placeholder="Search conditions or medications..."
            initialSearch={allItems.length === 0 ? prefill : ''}
          />
        </div>
      </div>
    );
  };

  // "Other" option per section — centered button that expands a search card
  const OtherOption = ({ sectionKey }) => {
    const otherKey = '__other_' + sectionKey;
    const isOpen = !!otherOpen[sectionKey];
    const allItems = [...getFieldItems(otherKey, 'conds'), ...getFieldItems(otherKey, 'meds')];
    const setAllItems = (updater) => {
      const updated = typeof updater === 'function' ? updater(allItems) : updater;
      setFieldItems(otherKey, 'conds')(() => updated.filter(i => i.type_id === 2));
      setFieldItems(otherKey, 'meds')(() => updated.filter(i => i.type_id === 1));
    };
    return (
      <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
        <button
          onClick={() => setOtherOpen(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
          style={{
            background: isOpen ? '#EFF6FF' : 'white',
            border: `1px solid ${isOpen ? '#2D87F0' : '#CBD5E1'}`,
            color: isOpen ? '#2D87F0' : '#64748B',
            borderRadius: '999px', padding: '6px 24px',
            fontSize: '14px', fontWeight: '500', cursor: 'pointer',
            transition: 'all 150ms',
          }}
        >
          <i className={`fas fa-${isOpen ? 'minus' : 'plus'} mr-2`} style={{ fontSize: '11px' }}></i>
          Other
        </button>
        {isOpen && (
          <div style={{ width: '50%', alignSelf: 'center' }}>
            <div className="portal-card" style={{ padding: '16px 20px' }}>
              <UwSearchSection
                uwItems={allItems}
                setUwItems={setAllItems}
                label="Enter a health condition or medication"
                placeholder="Search conditions or medications..."
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderField = (field) => {
    if (!checkVisibility(field)) return null;
    const isAnswered = !!answers[field.key];
    const isYes = answers[field.key] === 'yes';
    return (
      <div key={field.key}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', padding: '12px 16px', borderRadius: '8px', background: isAnswered ? '#EFF6FF' : '#F8FAFC', border: '1px solid ' + (isAnswered ? '#BFDBFE' : '#E2E8F0'), transition: 'background 200ms' }}>
          <label style={{ fontWeight: '500', fontSize: '15px', color: '#0F172A', flex: 1 }}>{field.title}</label>
          {field.type === 'yes_no' && <YesNoButtons name={'modal_' + field.key} value={answers[field.key] || ''} onChange={v => setAnswer(field.key, v)} />}
          {field.type === 'dropdown' && (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <select className="input-field" value={answers[field.key] || ''} onChange={e => setAnswer(field.key, e.target.value)} style={{ appearance: 'none', cursor: 'pointer', width: '200px', paddingRight: '32px', fontSize: '15px' }}>
                <option value="">Select...</option>
                {(field.values || []).map(v => <option key={v.key} value={v.key}>{v.title}</option>)}
              </select>
              <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
            </div>
          )}
        </div>
        {isYes && field.type === 'yes_no' && <FollowUp fieldKey={field.key} />}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)' }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #DDE7F1', width: '100%', maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--shadow-dropdown)' }}>

        {/* Header with tally */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 32px', borderBottom: '1px solid #DDE7F1', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontWeight: '700', fontSize: '18px', margin: 0 }}>Health &amp; Personal History</h2>
            <div style={{ fontSize: '13px', color: '#64748B', marginTop: '2px' }}>Pre-qualification questionnaire</div>
          </div>
          {/* Tally of recorded items */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {allConditions.length > 0 && (
              <button onClick={scrollToSummary} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '999px', padding: '4px 12px', cursor: 'pointer', transition: 'background 150ms' }}
                onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'}
                onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}>
                <i className="fas fa-book-medical" style={{ color: '#2D87F0', fontSize: '13px' }}></i>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2D87F0' }}>{allConditions.length}</span>
                <span style={{ fontSize: '13px', color: '#2D87F0' }}>condition{allConditions.length !== 1 ? 's' : ''}</span>
              </button>
            )}
            {allMedications.length > 0 && (
              <button onClick={scrollToSummary} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '999px', padding: '4px 12px', cursor: 'pointer', transition: 'background 150ms' }}
                onMouseEnter={e => e.currentTarget.style.background = '#DBEAFE'}
                onMouseLeave={e => e.currentTarget.style.background = '#EFF6FF'}>
                <i className="fas fa-prescription-bottle-medical" style={{ color: '#2D87F0', fontSize: '13px' }}></i>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#2D87F0' }}>{allMedications.length}</span>
                <span style={{ fontSize: '13px', color: '#2D87F0' }}>medication{allMedications.length !== 1 ? 's' : ''}</span>
              </button>
            )}
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#64748B', marginLeft: '8px' }}><i className="fas fa-times"></i></button>
          </div>
        </div>

        {/* Sticky tab bar — with Yes-answer count badges */}
        <div style={{ display: 'flex', borderBottom: '1px solid #DDE7F1', background: 'white', padding: '0 32px', flexShrink: 0 }}>
          {subsections.map((sub, i) => {
            const isActive = activeTab === i;
            const yesCount = sub.fields.filter(f => checkVisibility(f) && answers[f.key] === 'yes').length;
            return (
              <button key={sub.key} onClick={() => handleTabClick(i)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '13px 18px', fontSize: '14px', fontWeight: isActive ? '600' : '500', color: isActive ? '#2D87F0' : '#64748B', background: 'none', border: 'none', borderBottom: isActive ? '2px solid #2D87F0' : '2px solid transparent', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 150ms', marginBottom: '-1px' }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#334155'; }}
                onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748B'; }}
              >
                <i className={SECTION_ICONS[sub.key] || 'fas fa-circle'} style={{ fontSize: '14px' }}></i>
                {sub.title}
                {yesCount > 0 && (
                  <span
                    onClick={e => { e.stopPropagation(); scrollToSummary(); }}
                    style={{ fontSize: '11px', fontWeight: '700', background: isActive ? '#2D87F0' : '#EFF6FF', color: isActive ? 'white' : '#2D87F0', border: `1px solid ${isActive ? '#2D87F0' : '#BFDBFE'}`, borderRadius: '999px', padding: '1px 7px', marginLeft: '2px', cursor: 'pointer' }}
                  >
                    {yesCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable body — all sections open */}
        <div ref={scrollContainerRef} onScroll={handleScroll} style={{ overflowY: 'auto', flex: 1, padding: '24px 32px' }}>
          {subsections.map((sub, i) => {
            const visibleFields = sub.fields.filter(f => checkVisibility(f));
            const half = Math.ceil(visibleFields.length / 2);
            return (
              <div key={sub.key} ref={el => sectionRefs.current[i] = el} style={{ marginBottom: '40px', paddingBottom: '40px', borderBottom: i < subsections.length - 1 ? '1px solid #DDE7F1' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <i className={SECTION_ICONS[sub.key] || 'fas fa-circle'} style={{ color: '#2D87F0', fontSize: '18px' }}></i>
                  <span style={{ fontWeight: '700', fontSize: '16px', color: '#0F172A' }}>{sub.title}</span>
                </div>
                {sub.subsection_question && (
                  <div style={{ fontSize: '14px', color: '#475569', fontStyle: 'italic', marginBottom: '14px', paddingLeft: '28px' }}>
                    {sub.subsection_question}
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {visibleFields.slice(0, half).map(renderField)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {visibleFields.slice(half).map(renderField)}
                  </div>
                </div>

                {/* "Other" option — centered at bottom of section */}
                <OtherOption sectionKey={sub.key} />
              </div>
            );
          })}

          {/* Summary section */}
          {(allConditions.length > 0 || allMedications.length > 0) && (
            <div ref={summaryRef} style={{ marginTop: '32px', padding: '24px', background: '#F8FAFC', border: '1px solid #DDE7F1', borderRadius: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '12px', borderBottom: '1px solid #DDE7F1' }}>
                <i className="fas fa-clipboard-list" style={{ color: '#2D87F0', fontSize: '16px' }}></i>
                <span style={{ fontWeight: '700', fontSize: '16px', color: '#334155' }}>Summary</span>
                <span style={{ fontSize: '13px', color: '#64748B' }}>— entered conditions &amp; medications</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

                {/* Health Conditions — with edit/delete via UwItemRow */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                    <i className="fas fa-book-medical" style={{ color: '#2D87F0', marginRight: '6px' }}></i>Health Conditions
                  </div>
                  {allConditions.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>None entered</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Object.entries(fieldUwItems).map(([fieldKey, items]) =>
                        (items?.conds || []).map((item, idx) => (
                          <UwItemRow
                            key={fieldKey + '_c_' + idx}
                            item={item}
                            index={idx}
                            setUwItems={setFieldItems(fieldKey, 'conds')}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Medications — with edit/delete via UwItemRow */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                    <i className="fas fa-prescription-bottle-medical" style={{ color: '#2D87F0', marginRight: '6px' }}></i>Medications
                  </div>
                  {allMedications.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#94A3B8', fontStyle: 'italic' }}>None entered</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {Object.entries(fieldUwItems).map(([fieldKey, items]) =>
                        (items?.meds || []).map((item, idx) => (
                          <UwItemRow
                            key={fieldKey + '_m_' + idx}
                            item={item}
                            index={idx}
                            setUwItems={setFieldItems(fieldKey, 'meds')}
                          />
                        ))
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 32px', borderTop: '1px solid #DDE7F1', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="btn-primary" style={{ padding: '0 28px', height: '42px', fontSize: '15px' }} onClick={onClose}>Save &amp; Close</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// RESULTS ROW
// ============================================================
function QuoteResultRow({ row, companies, products, features, paymentMode, setPaymentMode, isSelected, onToggleSelect }) {
  const company = companies?.[row.company_id] || {};
  const product = products?.[row.product_id] || {};
  const monthlyParts = row.monthly_premium?.split('.') || ['--', '00'];
  const annualParts = row.annual_premium?.split('.') || ['--', '00'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 60px 60px', borderBottom: '1px solid #DDE7F1', opacity: row.hidden_reason ? 0.5 : 1, transition: 'opacity 150ms' }}
      onMouseEnter={e => row.hidden_reason && (e.currentTarget.style.opacity = '1')}
      onMouseLeave={e => row.hidden_reason && (e.currentTarget.style.opacity = '0.5')}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', gap: '8px' }}>
        {row.feature_codes?.includes('NE') && <span style={{ fontSize: '11px', fontWeight: '600', border: '1px solid #2D87F0', color: '#2D87F0', borderRadius: '4px', padding: '1px 6px', textTransform: 'uppercase' }}>No Exam</span>}
        {paymentMode === 'M' ? (
          <div style={{ whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', verticalAlign: 'top' }}>$</span>
            <span style={{ fontSize: '40px', fontWeight: '800', lineHeight: '0.8', verticalAlign: 'baseline' }}>{monthlyParts[0]}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{monthlyParts[1]}</span>
          </div>
        ) : (
          <div style={{ whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', verticalAlign: 'top' }}>$</span>
            <span style={{ fontSize: '40px', fontWeight: '800', lineHeight: '0.8', verticalAlign: 'baseline' }}>{annualParts[0]}</span>
            <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{annualParts[1]}</span>
          </div>
        )}
        <div style={{ position: 'relative', display: 'flex', background: '#E5E7EB', borderRadius: '999px', fontSize: '12px', fontWeight: '600', overflow: 'hidden', border: '1px solid #D1D5DB' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '50%', background: '#2D87F0', borderRadius: '999px', transform: paymentMode === 'A' ? 'translateX(100%)' : 'translateX(0)', transition: 'transform 200ms' }}></div>
          <button onClick={() => setPaymentMode('M')} style={{ width: '52px', padding: '4px', textAlign: 'center', zIndex: 1, color: paymentMode === 'M' ? 'white' : '#334155', border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Monthly</button>
          <button onClick={() => setPaymentMode('A')} style={{ width: '52px', padding: '4px', textAlign: 'center', zIndex: 1, color: paymentMode === 'A' ? 'white' : '#334155', border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>Annual</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', cursor: 'pointer', gap: '4px' }}>
        <div style={{ width: '120px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '6px', padding: '8px' }}>
          <div style={{ fontWeight: '700', fontSize: '14px', textAlign: 'center', color: '#334155' }}>{company.name}</div>
        </div>
        <span style={{ fontSize: '12px', color: '#2D87F0' }}>More Details</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white' }}>
        <div style={{ fontWeight: '600', fontSize: '14px', textAlign: 'center', color: row.hidden_reason ? '#DC2626' : '#334155', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px', whiteSpace: 'nowrap' }}>{product.name}</div>
        <div style={{ display: 'flex', gap: '4px', marginTop: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {(row.feature_codes || []).map(code => (
            <span key={code} title={features?.[code]?.description_short} style={{ width: '28px', height: '28px', borderRadius: '50%', border: '1px solid #2D87F0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#2D87F0', fontWeight: '600', cursor: 'pointer' }}>{code}</span>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white' }}>
        <span style={{ fontSize: '12px', color: '#94A3B8', border: '1px solid #DDE7F1', borderRadius: '4px', padding: '6px 8px', cursor: 'not-allowed' }}>App</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', background: 'white', borderRight: '1px solid #DDE7F1', borderRadius: '0 4px 4px 0' }}>
        <div style={{ width: '20px', height: '20px', borderRadius: '4px', border: `2px solid ${isSelected ? '#2D87F0' : '#DDE7F1'}`, background: isSelected ? '#2D87F0' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 150ms' }} onClick={() => onToggleSelect(row.key)}>
          {isSelected && <i className="fas fa-check text-white" style={{ fontSize: '11px' }}></i>}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN V2 PAGE
// ============================================================
const getRequiredFields = (engineId) => {
  const id = parseInt(engineId);
  if ([1, 2, 5, 10, 12, 14].includes(id)) return ['gender', 'coverage', 'term', 'state', 'nicotine', 'date_of_birth', 'height_weight', 'health'];
  if (id === 9)  return ['gender', 'state', 'date_of_birth', 'nicotine', 'coverage', 'height_weight'];
  if (id === 17) return ['gender', 'state', 'date_of_birth', 'nicotine', 'coverage'];
  if ([6, 7, 13].includes(id)) return ['gender', 'state', 'date_of_birth', 'coverage'];
  if ([4, 18].includes(id)) return ['gender', 'state', 'date_of_birth', 'height_weight', 'nicotine', 'coverage', 'health'];
  return ['gender', 'coverage', 'term', 'state', 'nicotine', 'date_of_birth', 'height_weight', 'health'];
};

// Short tab labels — "Life Insurance" and "Insurance" removed
const ENGINE_TAB_LABELS = {
  "1":  "Term Life",
  "2":  "No Medical Exam Term",
  "4":  "Guaranteed Universal Life",
  "5":  "Return of Premium",
  "6":  "Accidental Death Benefit",
  "7":  "Guaranteed Issue",
  "9":  "Final Expense",
  "10": "Living Benefits Term",
  "12": "Instant Issue Term",
  "13": "Children's Whole Life",
  "14": "Agentless Term",
  "17": "Whole Life",
  "18": "Multi-Product",
};

// Explicit display order: Term, FEX, Universal, Custom first — rest follow
const ENGINE_ORDER = ["1", "9", "4", "18", "2", "5", "6", "7", "10", "12", "13", "14", "17"];

export default function QuotePageV2() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const [details, setDetails] = useState({
    engine_id: '1', gender_code: 'M',
    dob_month: '01', dob_day: '01', dob_year: '1985',
    height_ft: '5', height_in: '10', weight: '175',
    state_code: 'TX', nicotine: '-1', health_class_code: 'PP',
    coverage_amount: '100000', category_code: '3',
  });
  const [coverageAmount, setCoverageAmount] = useState('100000');
  const [showCustomCoverage, setShowCustomCoverage] = useState(false);
  const [customCoverage, setCustomCoverage] = useState('');
  const [showCustomWeight, setShowCustomWeight] = useState(false);
  const [customWeight, setCustomWeight] = useState('');

  // UW state — split into medications and conditions
  const [uwMedItems, setUwMedItems] = useState([]);
  const [uwCondItems, setUwCondItems] = useState([]);
  const uwItems = [...uwMedItems, ...uwCondItems]; // combined for quote submission
  const [uwForm, setUwForm] = useState(null);
  const [uwModalVisible, setUwModalVisible] = useState(false);
  const [uwAnswers, setUwAnswers] = useState({});

  const [activeView, setActiveView] = useState('start');
  const [quoteInfoOpen, setQuoteInfoOpen] = useState(true);
  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [paymentMode, setPaymentMode] = useState('M');
  const [selectedRows, setSelectedRows] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '' });
  const [customerLink, setCustomerLink] = useState('');
  const [savingState, setSavingState] = useState(0);

  const requiredFields = getRequiredFields(details.engine_id);
  const allowMultipleCoverageTypes = details.engine_id === '18';

  // Days in selected month/year
  const getDaysInMonth = (month, year) => {
    if (!month) return 31;
    const m = parseInt(month);
    const y = parseInt(year) || new Date().getFullYear();
    return new Date(y, m, 0).getDate(); // day 0 of next month = last day of current month
  };
  const daysInMonth = getDaysInMonth(details.dob_month, details.dob_year);

  // Reset day if it exceeds days in selected month
  useEffect(() => {
    if (details.dob_day && parseInt(details.dob_day) > daysInMonth) {
      setDetails(d => ({ ...d, dob_day: String(daysInMonth).padStart(2, '0') }));
    }
  }, [details.dob_month, details.dob_year]);

  // For Custom engine: track multiple selected category codes
  const [selectedCategoryCodes, setSelectedCategoryCodes] = useState([]);
  const toggleCategoryCode = (value) => {
    setSelectedCategoryCodes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  useEffect(() => {
    async function fetchConfig() {
      try {
        const [configRes, uwRes] = await Promise.all([api.get('/quoter/config'), api.get('/quoter/uw-form')]);
        setConfig(configRes.data);
        setUwForm(uwRes.data.form);
        const defaultCov = configRes.data.coverage_amounts?.['1']?.find(c => c.is_default);
        if (defaultCov) { setDetails(d => ({ ...d, coverage_amount: defaultCov.value })); setCoverageAmount(defaultCov.value); }
        const defaultTerm = configRes.data.coverage_types?.['1']?.find(t => t.is_default);
        if (defaultTerm) setDetails(d => ({ ...d, category_code: defaultTerm.value }));
      } catch (e) { console.error(e); } finally { setLoading(false); }
    }
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!config) return;
    const amounts = config.coverage_amounts?.[details.engine_id] || [];
    const types = config.coverage_types?.[details.engine_id] || [];
    const defaultAmt = amounts.find(a => a.is_default) || amounts[0];
    const defaultType = types.find(t => t.is_default) || types[0];
    if (defaultAmt) { setDetails(d => ({ ...d, coverage_amount: defaultAmt.value })); setCoverageAmount(defaultAmt.value); }
    if (defaultType) setDetails(d => ({ ...d, category_code: defaultType.value }));
  }, [details.engine_id]);

  const validateForm = () => {
    const errs = {};
    if (!details.dob_year || !details.dob_month || !details.dob_day) errs.date_of_birth = 'Please enter a valid date of birth.';
    if (!details.state_code) errs.state = 'Please select a state.';
    if (requiredFields.includes('height_weight') && (!details.height_ft || !details.weight)) errs.height_weight = 'Please enter height and weight.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submitQuoteForm = async () => {
    setShowErrors(true);
    if (!validateForm()) return;
    setLoadingResults(true);
    setActiveView('results');
    setQuoteInfoOpen(false);
    setSelectedRows([]);
    try {
      const submitDetails = {
        ...details,
        coverage_amount: showCustomCoverage ? customCoverage.replace(/\D/g, '') : coverageAmount,
        // Custom engine: combine selected codes as Z:XYZ...
        ...(allowMultipleCoverageTypes && selectedCategoryCodes.length > 0
          ? { category_code: 'Z:' + selectedCategoryCodes.join('') }
          : {}),
      };
      const res = await api.post('/quoter/run-quote', { details: submitDetails });
      setResultsData(res.data);
    } catch (e) { toast.error('Error running quote. Please try again.'); }
    finally { setLoadingResults(false); }
  };

  const toggleRowSelect = (key) => setSelectedRows(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const getSelectedRowObjects = () => !resultsData?.rows ? [] : selectedRows.map(k => resultsData.rows[k]).filter(Boolean);

  const getSummaryString = () => {
    const coverageLabel = config?.coverage_amounts?.[details.engine_id]?.find(c => c.value === coverageAmount)?.title || `$${parseInt(coverageAmount || 0).toLocaleString()}`;
    const termLabel = config?.coverage_types?.[details.engine_id]?.find(t => t.value === details.category_code)?.title || '';
    const age = details.dob_year ? new Date().getFullYear() - parseInt(details.dob_year) : '';
    return [coverageLabel, termLabel, age ? `Age ${age}` : '', details.state_code].filter(Boolean).join(', ');
  };

  const handleSaveLead = async () => {
    if (savingState !== 0) return;
    setSavingState(1);
    try {
      await api.post('/quoter/save-lead', { customer, details, selected_rows: selectedRows });
      setSavingState(2);
      toast.success('Lead saved successfully!');
      setTimeout(() => setSavingState(0), 3000);
    } catch (e) { setSavingState(0); toast.error('Error saving lead.'); }
  };

  const handleGetCustomerLink = () => {
    const subdomain = config?.brand?.subdomain_alias || 'demo';
    const engineSlug = { '1': 'term-life', '2': 'final-expense', '3': 'whole-life', '4': 'universal-life' }[details.engine_id] || 'term-life';
    const params = new URLSearchParams();
    if (details.gender_code) params.set('gender', details.gender_code === 'M' ? 'Male' : 'Female');
    if (details.state_code) params.set('state', details.state_code);
    if (details.dob_year) params.set('dob', `${details.dob_year}-${(details.dob_month||'').padStart(2,'0')}-${(details.dob_day||'').padStart(2,'0')}`);
    if (details.coverage_amount) params.set('coverage_amount', details.coverage_amount);
    setCustomerLink(`https://${subdomain}.quoteplicity.com/quote/${engineSlug}?${params.toString()}`);
  };

  if (loading) {
    return <ProtectedLayout><div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px' }}><div className="skeleton" style={{ height: '400px', borderRadius: '12px' }}></div></div></ProtectedLayout>;
  }

  const engines = config?.engines || {};
  const coverageAmountsByEngine = config?.coverage_amounts || {};
  const coverageTypesByEngine = config?.coverage_types || {};
  const states = config?.states || {};
  const productFeatures = config?.product_features || {};
  const coverageAmountOptions = (coverageAmountsByEngine[details.engine_id] || []).map(c => ({ value: c.value, label: c.title }));
  const coverageTypeOptions = (coverageTypesByEngine[details.engine_id] || []).map(t => ({ value: t.value, label: t.title }));
  const stateOptions = Object.entries(states).map(([k, v]) => ({ value: k, label: v }));
  const visibleSections = resultsData?.sections || [];

  return (
    <ProtectedLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 16px', paddingBottom: selectedRows.length > 0 && activeView === 'results' ? '100px' : '24px' }}>

        {/* ===== START / RESULTS FORM ===== */}
        {(activeView === 'start' || activeView === 'results') && (
          <>
            <div style={{ marginBottom: '16px' }}>
              {/* Title row — aligned above respective columns */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <i className="fas fa-calculator" style={{ fontSize: '20px', color: '#2D87F0' }}></i>
                    <span style={{ fontSize: '20px', fontWeight: '700', color: '#2D87F0', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Live Quote</span>
                  </div>
                </div>
                <div style={{ width: '420px', flexShrink: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ flex: 1, height: '1px', background: '#DDE7F1' }}></div>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: '#2D87F0', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                      <i className="fas fa-magnifying-glass" style={{ fontSize: '18px', marginRight: '8px' }}></i>Quote IQ
                    </span>
                    <div style={{ flex: 1, height: '1px', background: '#DDE7F1' }}></div>
                  </div>
                </div>
              </div>

              {/* ===== OUTER ROW: left column (card+button) + UW cards ===== */}
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'flex-start' }}>

                {/* ── LEFT COLUMN: main card + Run Quote stacked ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>

                {/* ── MAIN QUOTE CARD — sidebar + form fields ── */}
                <div className="portal-card" style={{ display: 'flex', overflow: 'visible' }}>

                {/* ── LEFT SIDEBAR: vertical engine list grouped ── */}
                <div style={{ width: '220px', flexShrink: 0, borderRight: '1px solid #DDE7F1', display: 'flex', flexDirection: 'column', borderRadius: '12px 0 0 12px', overflow: 'hidden' }}>
                  {/* Sidebar header */}
                  <div style={{ padding: '12px 16px 8px', fontSize: '13px', fontWeight: '700', color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #DDE7F1' }}>
                    Product Type
                  </div>

                  {[
                    { label: 'Term',              ids: ['1','2','5','10','12','14'] },
                    { label: 'Final Expense / GI', ids: ['9','7'] },
                    { label: 'Permanent',          ids: ['4','17'] },
                    { label: 'Specialty',          ids: ['6','13'] },
                    { label: 'Multi-Product',      ids: ['18'] },
                  ].map((group) => (
                    <div key={group.label}>
                      {/* Group header — typographic only, no background */}
                      <div style={{ padding: '10px 16px 5px', fontSize: '11px', fontWeight: '700', color: '#475569', letterSpacing: '0.07em', textTransform: 'uppercase', borderBottom: '1px solid #DDE7F1', marginTop: '4px' }}>
                        {group.label}
                      </div>
                      {/* Engines in this group */}
                      {group.ids.map(id => {
                        const engine = engines[id];
                        if (!engine) return null;
                        const isActive = details.engine_id === engine.id;
                        return (
                          <button
                            key={engine.id}
                            onClick={() => setDetails(d => ({ ...d, engine_id: engine.id }))}
                            style={{
                              display: 'block', width: '100%', textAlign: 'left',
                              padding: '11px 16px 11px 24px',
                              fontSize: '13px',
                              fontWeight: isActive ? '600' : '500',
                              color: isActive ? '#2D87F0' : '#1E293B',
                              background: isActive ? '#EFF6FF' : 'none',
                              border: 'none',
                              borderLeft: isActive ? '3px solid #2D87F0' : '3px solid transparent',
                              cursor: 'pointer',
                              transition: 'background 150ms, color 150ms',
                              lineHeight: '1.35',
                            }}
                            onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = '#F8FAFC'; e.currentTarget.style.color = '#2D87F0'; } }}
                            onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#1E293B'; } }}
                          >
                            {ENGINE_TAB_LABELS[engine.id] || engine.title}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {/* ── RIGHT: form fields ── */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

                  {/* Form header — vertically aligned with PRODUCT TYPE sidebar header */}
                  <div style={{ padding: '12px 24px 8px', fontSize: '13px', fontWeight: '700', color: '#475569', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid #DDE7F1' }}>
                    Coverage Details
                  </div>

                {/* ── SINGLE COLUMN ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '16px 24px' }}>

                  {/* Coverage amount */}
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Coverage amount</div>
                    <div style={{ position: 'relative' }}>
                      <select value={showCustomCoverage ? 'custom' : coverageAmount} onChange={e => { if (e.target.value === 'custom') { setShowCustomCoverage(true); } else { setShowCustomCoverage(false); setCoverageAmount(e.target.value); setDetails(d => ({ ...d, coverage_amount: e.target.value })); } }} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '36px' }}>
                        {coverageAmountOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        <option value="custom">{showCustomCoverage && customCoverage ? `Custom (${customCoverage})` : 'Custom'}</option>
                      </select>
                      <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                    </div>
                    {showCustomCoverage && (
                      <input type="text" className="input-field" style={{ marginTop: '8px' }} placeholder="Enter coverage amount" value={customCoverage} onChange={e => { const raw = e.target.value.replace(/\D/g, ''); setCustomCoverage(raw ? '$' + parseInt(raw).toLocaleString() : ''); setDetails(d => ({ ...d, coverage_amount: raw })); }} />
                    )}
                  </div>

                  {/* ── PERSONAL DETAILS ── */}
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', letterSpacing: '0.08em', textTransform: 'uppercase', paddingBottom: '4px', borderBottom: '1px solid #F1F5F9', marginTop: '4px' }}>
                    Personal Details
                  </div>

                  {/* Date of birth */}
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Date of birth</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {/* Month */}
                      <div style={{ position: 'relative', flex: 1 }}>
                        <select value={details.dob_month} onChange={e => setDetails(d => ({ ...d, dob_month: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                          <option value="">MM</option>
                          {[['01','January'],['02','February'],['03','March'],['04','April'],['05','May'],['06','June'],['07','July'],['08','August'],['09','September'],['10','October'],['11','November'],['12','December']].map(([v,l]) => (
                            <option key={v} value={v}>{l}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                      </div>
                      {/* Day */}
                      <div style={{ position: 'relative', flex: 1 }}>
                        <select value={details.dob_day} onChange={e => setDetails(d => ({ ...d, dob_day: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                          <option value="">DD</option>
                          {Array.from({ length: daysInMonth }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                      </div>
                      {/* Year */}
                      <div style={{ position: 'relative', flex: 2 }}>
                        <select value={details.dob_year} onChange={e => setDetails(d => ({ ...d, dob_year: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                          <option value="">YYYY</option>
                          {Array.from({ length: new Date().getFullYear() - 1919 }, (_, i) => String(new Date().getFullYear() - i)).map(y => (
                            <option key={y} value={y}>{y}</option>
                          ))}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                    {showErrors && errors.date_of_birth && <div style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px', fontWeight: '600' }}>{errors.date_of_birth}</div>}
                  </div>

                  {/* Gender */}
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Gender</div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <GenderButton value="M" selected={details.gender_code === 'M'} onChange={v => setDetails(d => ({ ...d, gender_code: v }))} />
                      <GenderButton value="F" selected={details.gender_code === 'F'} onChange={v => setDetails(d => ({ ...d, gender_code: v }))} />
                    </div>
                  </div>

                  {/* Term dropdown OR Custom checkboxes */}
                  {(allowMultipleCoverageTypes || (requiredFields.includes('term') && coverageTypeOptions.length > 1)) && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>
                        {allowMultipleCoverageTypes ? 'Coverage Types' : 'Term'}
                      </div>

                      {allowMultipleCoverageTypes ? (
                        /* Custom engine: checkboxes grouped by category */
                        (() => {
                          const groups = [
                            { label: 'Term', values: ['1','2','3','4','5','6','7'] },
                            { label: 'Return of Premium', values: ['J','K','L','M'] },
                            { label: 'Permanent', values: ['A','B','8','C','D','E','F','G','Y'] },
                          ];
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                              {groups.map(group => {
                                const groupOpts = coverageTypeOptions.filter(o => group.values.includes(o.value));
                                if (!groupOpts.length) return null;
                                return (
                                  <div key={group.label}>
                                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: '8px' }}>
                                      {group.label}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                                      {groupOpts.map(opt => {
                                        const checked = selectedCategoryCodes.includes(opt.value);
                                        return (
                                          <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '8px 12px', borderRadius: '8px', border: `1px solid ${checked ? '#2D87F0' : '#DDE7F1'}`, background: checked ? '#EFF6FF' : 'white', transition: 'all 150ms' }}>
                                            <div style={{ width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, border: `2px solid ${checked ? '#2D87F0' : '#CBD5E1'}`, background: checked ? '#2D87F0' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 150ms' }}>
                                              {checked && <i className="fas fa-check" style={{ fontSize: '10px', color: 'white' }}></i>}
                                            </div>
                                            <input type="checkbox" value={opt.value} checked={checked} onChange={() => toggleCategoryCode(opt.value)} style={{ display: 'none' }} />
                                            <span style={{ fontSize: '14px', fontWeight: checked ? '500' : '400', color: checked ? '#2D87F0' : '#334155' }}>{opt.label}</span>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                              {selectedCategoryCodes.length === 0 && (
                                <div style={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic' }}>Select one or more coverage types</div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        /* All other engines: single dropdown */
                        <div style={{ position: 'relative' }}>
                          <select value={details.category_code} onChange={e => setDetails(d => ({ ...d, category_code: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '36px' }}>
                            {coverageTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                          <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                        </div>
                      )}
                    </div>
                  )}

                  {/* State */}
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>State</div>
                    <div style={{ position: 'relative' }}>
                      <select className="input-field" value={details.state_code} onChange={e => setDetails(d => ({ ...d, state_code: e.target.value }))} style={{ appearance: 'none', cursor: 'pointer', paddingRight: '36px' }}>
                        <option value="">Select a state</option>
                        {stateOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                    </div>
                    {showErrors && errors.state && <div style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px', fontWeight: '600' }}>{errors.state}</div>}
                  </div>

                  {/* Health class (conditional) */}
                  {requiredFields.includes('health') && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Health class</div>
                      <div style={{ position: 'relative' }}>
                        <select value={details.health_class_code} onChange={e => setDetails(d => ({ ...d, health_class_code: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '36px' }}>
                          {HEALTH_CLASSES.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                  )}

                  {/* Build / Height-Weight (conditional) */}
                  {requiredFields.includes('height_weight') && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Build</div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#64748B' }}>Height</span>
                        {/* Feet */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <select value={details.height_ft} onChange={e => setDetails(d => ({ ...d, height_ft: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                            <option value="">ft</option>
                            {['4','5','6','7'].map(v => <option key={v} value={v}>{v} ft</option>)}
                          </select>
                          <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                        </div>
                        {/* Inches */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <select value={details.height_in} onChange={e => setDetails(d => ({ ...d, height_in: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                            <option value="">in</option>
                            {Array.from({ length: 12 }, (_, i) => String(i)).map(v => <option key={v} value={v}>{v} in</option>)}
                          </select>
                          <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                        </div>
                        <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#64748B' }}>Weight</span>
                        {/* Weight */}
                        <div style={{ position: 'relative', flex: 1 }}>
                          <select value={showCustomWeight ? 'custom' : details.weight} onChange={e => {
                            if (e.target.value === 'custom') { setShowCustomWeight(true); }
                            else { setShowCustomWeight(false); setCustomWeight(''); setDetails(d => ({ ...d, weight: e.target.value })); }
                          }} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '28px' }}>
                            <option value="">lbs</option>
                            <option value="custom">Custom</option>
                            {Array.from({ length: 65 }, (_, i) => String(80 + i * 5)).map(v => <option key={v} value={v}>{v} lbs</option>)}
                            <option value="custom">Custom</option>
                          </select>
                          <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '11px', pointerEvents: 'none' }}></i>
                        </div>
                      </div>
                      {showCustomWeight && (
                        <input
                          type="tel"
                          className="input-field"
                          placeholder="Enter weight in lbs"
                          value={customWeight}
                          onChange={e => {
                            const raw = e.target.value.replace(/\D/g, '');
                            setCustomWeight(raw);
                            setDetails(d => ({ ...d, weight: raw }));
                          }}
                          style={{ marginTop: '8px' }}
                        />
                      )}
                      {showErrors && errors.height_weight && <div style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px', fontWeight: '600' }}>{errors.height_weight}</div>}
                    </div>
                  )}

                  {/* Nicotine (conditional) */}
                  {requiredFields.includes('nicotine') && (
                    <div>
                      <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '14px' }}>Nicotine use</div>
                      <div style={{ position: 'relative' }}>
                        <select value={details.nicotine} onChange={e => setDetails(d => ({ ...d, nicotine: e.target.value }))} className="input-field" style={{ appearance: 'none', cursor: 'pointer', paddingRight: '36px' }}>
                          {NICOTINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <i className="fas fa-chevron-down" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#64748B', fontSize: '12px', pointerEvents: 'none' }}></i>
                      </div>
                    </div>
                  )}

                </div>
                </div>{/* end right panel */}

                </div>{/* end main quote card */}

                {/* ── RUN QUOTE — subtle action button ── */}
                <div style={{ marginTop: '28px', display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={submitQuoteForm}
                    data-testid="run-quote-button"
                    className="btn-secondary"
                    style={{
                      width: '280px',
                      padding: '12px 0',
                      fontSize: '17px',
                      fontWeight: '600',
                      borderRadius: '8px',
                    }}
                  >
                    <i className="fas fa-play-circle" style={{ marginRight: '10px', fontSize: '18px' }}></i>Run Quote
                  </button>
                </div>

                </div>{/* end left column */}

                {/* ── RIGHT COLUMN: Quote IQ header + two UW cards + pre-qual button ── */}
                <div style={{ width: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Health conditions card */}
                  <div className="portal-card" style={{ padding: '20px 16px' }}>
                    <UwSearchSection
                      uwItems={uwCondItems}
                      setUwItems={setUwCondItems}
                      typeId={2}
                      label="Enter a health condition"
                      placeholder="Search conditions..."
                    />
                  </div>

                  {/* Medications card */}
                  <div className="portal-card" style={{ padding: '20px 16px' }}>
                    <UwSearchSection
                      uwItems={uwMedItems}
                      setUwItems={setUwMedItems}
                      typeId={1}
                      label="Enter medication"
                      placeholder="Search medications..."
                    />
                  </div>

                  {/* Pre-qualification Questionnaire button */}
                  <button
                    onClick={() => setUwModalVisible(true)}
                    className="btn-secondary"
                    style={{ padding: '14px 24px', fontSize: '15px', borderRadius: '8px', width: '100%', marginTop: '8px' }}
                  >
                    <i className="fas fa-edit mr-2"></i>Pre-qualification Questionnaire
                  </button>

                  {/* Completed items — grouped by type with section headers */}
                  {([...uwCondItems, ...uwMedItems].some(i => i.completed)) && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>

                      {/* Health Conditions */}
                      {uwCondItems.some(i => i.completed) && (
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 4px' }}>
                            <i className="fas fa-book-medical" style={{ color: '#2D87F0', fontSize: '20px' }}></i>
                            Health Conditions
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {uwCondItems.filter(i => i.completed).map((item) => {
                              const realIndex = uwCondItems.indexOf(item);
                              return <UwItemRow key={item.id + realIndex} item={item} index={realIndex} setUwItems={setUwCondItems} />;
                            })}
                          </div>
                        </div>
                      )}

                      {/* Medications */}
                      {uwMedItems.some(i => i.completed) && (
                        <div style={{ marginTop: '8px' }}>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', padding: '0 4px' }}>
                            <i className="fas fa-prescription-bottle-medical" style={{ color: '#2D87F0', fontSize: '20px' }}></i>
                            Medications
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {uwMedItems.filter(i => i.completed).map((item) => {
                              const realIndex = uwMedItems.indexOf(item);
                              return <UwItemRow key={item.id + realIndex} item={item} index={realIndex} setUwItems={setUwMedItems} />;
                            })}
                          </div>
                        </div>
                      )}

                    </div>
                  )}

                </div>

              </div>{/* end outer row */}
            </div>

            {/* ===== RESULTS VIEW ===== */}
            {activeView === 'results' && (
              <div>
                {/* Quick edit controls */}
                {!loadingResults && resultsData && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '14px', color: '#64748B' }}>Coverage:</div>
                      <select className="input-field" value={showCustomCoverage ? 'custom' : coverageAmount} onChange={e => { if (e.target.value === 'custom') { setShowCustomCoverage(true); } else { setShowCustomCoverage(false); setCoverageAmount(e.target.value); setDetails(d => ({ ...d, coverage_amount: e.target.value })); submitQuoteForm(); } }} style={{ appearance: 'none', cursor: 'pointer' }}>
                        {coverageAmountOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    {requiredFields.includes('term') && coverageTypeOptions.length > 1 && (
                      <div>
                        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#64748B' }}>Term:</div>
                        <select className="input-field" value={details.category_code} onChange={e => { setDetails(d => ({ ...d, category_code: e.target.value })); setTimeout(submitQuoteForm, 50); }} style={{ appearance: 'none', cursor: 'pointer' }}>
                          {coverageTypeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    )}
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '14px', color: '#64748B' }}>Payment type:</div>
                      <select className="input-field" value={paymentMode} onChange={e => setPaymentMode(e.target.value)} style={{ appearance: 'none', cursor: 'pointer' }}>
                        <option value="M">Monthly</option>
                        <option value="A">Annual</option>
                      </select>
                    </div>
                    <div>
                      <div style={{ marginBottom: '8px', fontSize: '14px', color: '#64748B' }}>&nbsp;</div>
                      <button onClick={() => setFiltersOpen(!filtersOpen)} style={{ width: '100%', height: '40px', borderRadius: '8px', border: `1px solid #2D87F0`, background: filtersOpen ? '#2D87F0' : 'white', color: filtersOpen ? 'white' : '#2D87F0', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>Filter Results</button>
                    </div>
                  </div>
                )}

                {loadingResults && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {[1, 2, 3, 4].map(i => <div key={i} className="skeleton" style={{ height: '100px', borderRadius: '8px' }}></div>)}
                  </div>
                )}

                {!loadingResults && resultsData && (
                  <>
                    {visibleSections.length === 0 ? (
                      <div className="portal-card" style={{ padding: '32px', textAlign: 'center', fontWeight: '600', fontSize: '16px' }}>Your search returned no results.</div>
                    ) : (
                      <div className="portal-card" style={{ overflow: 'hidden' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 60px 60px', background: '#F8FAFC', borderBottom: '1px solid #DDE7F1' }}>
                          {['Price', 'Company', 'Product & Features', 'Actions', 'Select'].map(h => (
                            <div key={h} style={{ padding: '12px 16px', fontWeight: '600', fontSize: '14px', textAlign: 'center', borderLeft: '1px solid #DDE7F1', borderRight: h === 'Select' ? '1px solid #DDE7F1' : 'none' }}>{h}</div>
                          ))}
                        </div>
                        {visibleSections.map((section, si) => (
                          <div key={section.key}>
                            {section.title && <div style={{ textAlign: 'center', fontSize: '24px', fontWeight: '800', padding: '16px', borderBottom: '1px solid #DDE7F1', marginTop: si > 0 ? '16px' : 0 }}>{section.title}</div>}
                            {(section.rows || []).map(rowKey => {
                              const row = resultsData.rows?.[rowKey];
                              if (!row) return null;
                              return <QuoteResultRow key={rowKey} row={row} companies={resultsData.companies} products={resultsData.products} features={productFeatures} paymentMode={paymentMode} setPaymentMode={setPaymentMode} isSelected={selectedRows.includes(rowKey)} onToggleSelect={toggleRowSelect} />;
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ===== REVIEW SELECTED ===== */}
        {activeView === 'review' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '28px' }}>
              <button onClick={() => setActiveView('results')} style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #334155', borderRadius: '6px', background: 'white', cursor: 'pointer' }}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <div style={{ marginLeft: '14px', fontWeight: '600', fontSize: '20px' }}>Review Selected Policies</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {getSelectedRowObjects().map(row => {
                  const company = resultsData?.companies?.[row.company_id] || {};
                  const product = resultsData?.products?.[row.product_id] || {};
                  const monthlyParts = row.monthly_premium?.split('.') || ['--', '00'];
                  return (
                    <div key={row.key} className="portal-card" style={{ display: 'flex', alignItems: 'center', gap: '20px', padding: '20px 24px' }}>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '12px', verticalAlign: 'top' }}>$</span>
                        <span style={{ fontSize: '36px', fontWeight: '800', verticalAlign: 'baseline' }}>{monthlyParts[0]}</span>
                        <span style={{ fontSize: '18px', fontWeight: '700', verticalAlign: 'baseline' }}>.{monthlyParts[1]}</span>
                        <span style={{ fontSize: '12px', verticalAlign: 'baseline' }}>/mo</span>
                      </div>
                      <div style={{ width: '120px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '6px', padding: '8px' }}>
                        <div style={{ fontWeight: '700', fontSize: '12px', textAlign: 'center' }}>{company.name}</div>
                      </div>
                      <div style={{ flex: 1, fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                      <div style={{ color: '#2D87F0', fontWeight: '600', fontSize: '14px' }}>Selected</div>
                    </div>
                  );
                })}
              </div>
              <div className="portal-card" style={{ padding: '24px', height: 'fit-content' }}>
                <div style={{ fontWeight: '700', fontSize: '16px', marginBottom: '20px' }}>Save &amp; Share</div>
                <hr style={{ borderColor: '#DDE7F1', marginBottom: '20px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div><div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>Customer Name</div><input className="input-field" type="text" placeholder="Customer Name" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} /></div>
                  <div><div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>Email</div><input className="input-field" type="email" placeholder="Customer Email" value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} /></div>
                  <div><div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>Phone</div><input className="input-field" type="tel" placeholder="(xxx) xxx-xxxx" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} /></div>
                  <hr style={{ borderColor: '#DDE7F1' }} />
                  {!customerLink ? (
                    <button onClick={handleGetCustomerLink} className="btn-secondary" style={{ width: '100%', height: '48px', fontSize: '14px' }}>Get Customer Link</button>
                  ) : (
                    <a href={customerLink} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: '#2D87F0', wordBreak: 'break-all' }}>{customerLink}</a>
                  )}
                  <hr style={{ borderColor: '#DDE7F1' }} />
                  <button onClick={handleSaveLead} disabled={savingState !== 0} className="btn-primary" style={{ width: '100%', height: '48px', fontSize: '14px', opacity: savingState !== 0 ? 0.7 : 1 }}>{['Save as Lead', 'Saving...', 'Saved!'][savingState]}</button>
                  <button onClick={() => { setActiveView('start'); setQuoteInfoOpen(true); setSelectedRows([]); setResultsData(null); }} className="btn-secondary" style={{ width: '100%', height: '48px', fontSize: '14px' }}>Start Over</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky continue bar */}
      {selectedRows.length > 0 && activeView === 'results' && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '72px', background: 'white', borderTop: '1px solid #DDE7F1', boxShadow: '0 -4px 20px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 24px', gap: '16px', zIndex: 49 }} data-testid="sticky-continue-bar">
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center', overflow: 'hidden', paddingRight: '160px' }}>
            {getSelectedRowObjects().map(row => {
              const company = resultsData?.companies?.[row.company_id] || {};
              return <div key={row.key} style={{ height: '40px', padding: '4px 12px', background: '#F8FAFC', borderRadius: '6px', display: 'flex', alignItems: 'center', fontWeight: '600', fontSize: '12px', border: '1px solid #DDE7F1', whiteSpace: 'nowrap' }}>{company.name}</div>;
            })}
          </div>
          <button onClick={() => setActiveView('review')} className="btn-primary" style={{ padding: '0 32px', height: '44px', fontSize: '15px', whiteSpace: 'nowrap' }} data-testid="continue-button">Continue ({selectedRows.length})</button>
          <button onClick={() => setSelectedRows([])} style={{ background: 'none', border: 'none', color: '#2D87F0', cursor: 'pointer', fontSize: '14px', whiteSpace: 'nowrap', marginRight: '160px' }} data-testid="deselect-all-button">Deselect All</button>
        </div>
      )}

      <UwHistoryModal visible={uwModalVisible} onClose={() => setUwModalVisible(false)} form={uwForm} answers={uwAnswers} setAnswers={setUwAnswers} />
    </ProtectedLayout>
  );
}
